import React, { useCallback } from 'react';
import { Button, InputNumber, Select, Spin, Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';
import { useTemplatePrintDefaults } from '../hooks/useTemplatePrintDefaults';
import {
  getPrintCatalogLabelForValue,
  printCatalogFieldToDimension,
  sortPrintCatalogEntries,
} from '../../../../utils/services/printDefaultsAPI';
import {
  DescriptionText,
  FieldBlock,
  FieldLabel,
  FooterRow,
  InheritedText,
  LoadingContainer,
  OverrideControlWrapper,
  SettingRow,
  TemplatePrintDefaultsContainer,
  ToggleLabel,
} from './TemplatePrintDefaultsSection.styles';

const { Option } = Select;
const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const CATALOG_OVERRIDE_KEYS = ['colorMode', 'mailType', 'envelopeType'];

function isDraftFieldOverridden(key, draft) {
  const v = draft[key];
  return v !== null && v !== undefined;
}

const TemplatePrintDefaultsSection = ({ readOnly }) => {
  const { t } = useTranslation(NAMESPACES);
  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, 'TemplatePrintDefaultsSection');

  const {
    orgDefaults,
    catalogMap,
    organizationId,
    draft,
    setDraft,
    save,
    reset,
    loading,
    saving,
    dirty,
  } = useTemplatePrintDefaults(documentId);

  const setDraftKey = useCallback(
    (key, value) => {
      setDraft(prev => ({ ...prev, [key]: value }));
    },
    [setDraft],
  );

  const catalogEntriesForField = useCallback(
    fieldKey => {
      if (!organizationId || catalogMap == null) {
        return null;
      }
      const dim = printCatalogFieldToDimension(fieldKey);
      if (!dim) {
        return null;
      }
      return sortPrintCatalogEntries(catalogMap[dim] || []);
    },
    [catalogMap, organizationId],
  );

  const renderCatalogSelect = (fieldKey, value, onChange) => {
    const notSetPh = t(
      DESIGN_AREA_SETTINGS_TAB.TPD_ORG_DEFAULT_NOT_SET.key,
      DESIGN_AREA_SETTINGS_TAB.TPD_ORG_DEFAULT_NOT_SET.value,
    );
    const emptyCatalogPh = t(
      DESIGN_AREA_SETTINGS_TAB.TPD_CATALOG_EMPTY_PLACEHOLDER.key,
      DESIGN_AREA_SETTINGS_TAB.TPD_CATALOG_EMPTY_PLACEHOLDER.value,
    );
    const entries = catalogEntriesForField(fieldKey);
    if (entries == null) {
      return (
        <Select value={undefined} placeholder={notSetPh} disabled={readOnly || saving} />
      );
    }
    const inCatalog =
      value != null &&
      String(value) !== '' &&
      entries.some(e => String(e.value) === String(value));
    const selectValue = inCatalog ? value : undefined;
    return (
      <Select
        value={entries.length ? selectValue : undefined}
        placeholder={entries.length ? undefined : emptyCatalogPh}
        onChange={v => onChange(v != null && v !== '' ? v : '')}
        disabled={readOnly || saving}
        allowClear={!!entries.length}
      >
        {entries.map(row => (
          <Option key={String(row.id != null ? row.id : row.value)} value={row.value}>
            {row.displayLabel != null && row.displayLabel !== '' ? row.displayLabel : row.value}
          </Option>
        ))}
      </Select>
    );
  };

  const notSetLabel = useCallback(
    () =>
      t(DESIGN_AREA_SETTINGS_TAB.TPD_ORG_DEFAULT_NOT_SET.key, DESIGN_AREA_SETTINGS_TAB.TPD_ORG_DEFAULT_NOT_SET.value),
    [t],
  );

  const getInheritedValue = useCallback(
    key => {
      if (!orgDefaults) {
        return notSetLabel();
      }
      const rawValue = orgDefaults[key];
      if (key === 'returnEnvelope') {
        if (rawValue === null || rawValue === undefined) {
          return notSetLabel();
        }
        return rawValue
          ? t(DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_YES.key, DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_YES.value)
          : t(DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_NO.key, DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_NO.value);
      }
      if (key === 'paperWeight') {
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          return notSetLabel();
        }
        return String(rawValue);
      }
      const dim = printCatalogFieldToDimension(key);
      if (dim && catalogMap) {
        const entries = catalogMap[dim] || [];
        const fromCatalog = getPrintCatalogLabelForValue(entries, rawValue);
        if (fromCatalog) {
          return fromCatalog;
        }
      }
      if (dim && (rawValue === null || rawValue === undefined || rawValue === '')) {
        return notSetLabel();
      }
      if (dim) {
        return notSetLabel();
      }
      return String(rawValue);
    },
    [catalogMap, notSetLabel, orgDefaults, t],
  );

  const handleSave = async () => {
    const response = await save();
    if (response.success) {
      message.success(t(DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_SUCCESS.key, DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_SUCCESS.value));
    } else {
      message.error(t(DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_ERROR.key, DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_ERROR.value));
    }
  };

  const handleReset = async () => {
    const response = await reset();
    if (response.success) {
      message.success(
        t(DESIGN_AREA_SETTINGS_TAB.TPD_RESET_SUCCESS.key, DESIGN_AREA_SETTINGS_TAB.TPD_RESET_SUCCESS.value),
      );
    } else {
      message.error(t(DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_ERROR.key, DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_ERROR.value));
    }
  };

  const renderField = (key, title, control) => {
    const isOverridden = isDraftFieldOverridden(key, draft);
    const seedOverrideValue = k => {
      if (CATALOG_OVERRIDE_KEYS.includes(k)) {
        const v = orgDefaults?.[k];
        return v != null && v !== '' ? v : '';
      }
      if (k === 'paperWeight') {
        const v = orgDefaults?.paperWeight;
        return v != null && v !== '' ? v : null;
      }
      if (k === 'returnEnvelope') {
        const v = orgDefaults?.returnEnvelope;
        return v !== null && v !== undefined ? v : false;
      }
      return orgDefaults?.[k] ?? null;
    };
    return (
      <FieldBlock key={key}>
        <SettingRow>
          <FieldLabel>{title}</FieldLabel>
          <ToggleLabel>
            {t(
              DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_OVERRIDE_LABEL.key,
              DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_OVERRIDE_LABEL.value,
            )}
            <Switch
              checked={isOverridden}
              onChange={checked => {
                if (checked) {
                  setDraftKey(key, seedOverrideValue(key));
                } else {
                  setDraftKey(key, null);
                }
              }}
              disabled={readOnly || saving}
            />
          </ToggleLabel>
        </SettingRow>
        {!isOverridden ? (
          <InheritedText>
            {t(
              DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_USE_ORG_DEFAULT.key,
              DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_USE_ORG_DEFAULT.value,
              { value: getInheritedValue(key) },
            )}
          </InheritedText>
        ) : (
          <OverrideControlWrapper>{control}</OverrideControlWrapper>
        )}
      </FieldBlock>
    );
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="small" />
      </LoadingContainer>
    );
  }

  if (!documentId) {
    return (
      <TemplatePrintDefaultsContainer>
        <DescriptionText>
          {t(DESIGN_AREA_SETTINGS_TAB.TPD_FETCH_ERROR.key, DESIGN_AREA_SETTINGS_TAB.TPD_FETCH_ERROR.value)}
        </DescriptionText>
      </TemplatePrintDefaultsContainer>
    );
  }

  const colorModeValue = draft.colorMode ?? orgDefaults?.colorMode;
  const paperWeightValue = draft.paperWeight ?? orgDefaults?.paperWeight;
  const mailTypeValue = draft.mailType ?? orgDefaults?.mailType;
  const envelopeTypeValue = draft.envelopeType ?? orgDefaults?.envelopeType;
  const returnEnvelopeRaw = draft.returnEnvelope ?? orgDefaults?.returnEnvelope;
  const returnEnvelopeValue = returnEnvelopeRaw === true;

  return (
    <TemplatePrintDefaultsContainer>
      <DescriptionText>
        {t(
          DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_DESCRIPTION.key,
          DESIGN_AREA_SETTINGS_TAB.TEMPLATE_PRINT_DEFAULTS_DESCRIPTION.value,
        )}
      </DescriptionText>

      {renderField(
        'colorMode',
        t(DESIGN_AREA_SETTINGS_TAB.TPD_COLOR_MODE.key, DESIGN_AREA_SETTINGS_TAB.TPD_COLOR_MODE.value),
        renderCatalogSelect('colorMode', colorModeValue, v => setDraftKey('colorMode', v)),
      )}

      {renderField(
        'paperWeight',
        t(DESIGN_AREA_SETTINGS_TAB.TPD_PAPER_WEIGHT.key, DESIGN_AREA_SETTINGS_TAB.TPD_PAPER_WEIGHT.value),
        <InputNumber
          min={1}
          max={500}
          value={paperWeightValue == null ? undefined : paperWeightValue}
          onChange={value => setDraftKey('paperWeight', value == null ? null : value)}
          formatter={value => (value != null ? `${value} g/m²` : '')}
          parser={value => value.replace(/\s*g\/m²\s*/g, '')}
          disabled={readOnly || saving}
        />,
      )}

      {renderField(
        'mailType',
        t(DESIGN_AREA_SETTINGS_TAB.TPD_MAIL_TYPE.key, DESIGN_AREA_SETTINGS_TAB.TPD_MAIL_TYPE.value),
        renderCatalogSelect('mailType', mailTypeValue, v => setDraftKey('mailType', v)),
      )}

      {renderField(
        'envelopeType',
        t(DESIGN_AREA_SETTINGS_TAB.TPD_ENVELOPE_TYPE.key, DESIGN_AREA_SETTINGS_TAB.TPD_ENVELOPE_TYPE.value),
        renderCatalogSelect('envelopeType', envelopeTypeValue, v => setDraftKey('envelopeType', v)),
      )}

      {renderField(
        'returnEnvelope',
        t(DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_ENVELOPE.key, DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_ENVELOPE.value),
        <Switch
          checked={!!returnEnvelopeValue}
          onChange={checked => setDraftKey('returnEnvelope', checked)}
          checkedChildren={t(DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_YES.key, DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_YES.value)}
          unCheckedChildren={t(DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_NO.key, DESIGN_AREA_SETTINGS_TAB.TPD_RETURN_NO.value)}
          disabled={readOnly || saving}
        />,
      )}

      <FooterRow>
        <Button onClick={handleReset} disabled={readOnly || saving} icon="reload">
          {t(DESIGN_AREA_SETTINGS_TAB.TPD_RESET_BUTTON.key, DESIGN_AREA_SETTINGS_TAB.TPD_RESET_BUTTON.value)}
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={readOnly || !dirty}
          icon="save"
        >
          {t(DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_BUTTON.key, DESIGN_AREA_SETTINGS_TAB.TPD_SAVE_BUTTON.value)}
        </Button>
      </FooterRow>
    </TemplatePrintDefaultsContainer>
  );
};

export default TemplatePrintDefaultsSection;
